import { CONTRACT_SCHEMA_VERSION } from "../domain/common";
import type {
  AuditEvent,
  CEOProfile,
  ConstitutionVersion,
  ContractEnvelope,
  DataExportManifest,
  DataImportResult,
  DatabaseMetaRecord,
  ExternalSummaryRecord,
  PrivateSourceRecord,
  StoredBoardContext,
  StoredJointCorrection,
  StructuredCEORecord,
} from "../domain/types";
import { sha256, verifyPayloadHash } from "../contracts/canonical-json";
import { validateContract } from "../contracts/validator";
import { createAuditEvent } from "../db/audit";
import {
  DATABASE_VERSION,
  normalizeRound03ProfileRecord,
  normalizeRound03StructuredRecord,
  type CEOOSDatabase,
} from "../db/database";
import { RuleErrorCode } from "../rules/errors";
import { FULL_BACKUP_WARNING } from "../privacy/policy";

export interface FullBackupData {
  privateSources: PrivateSourceRecord[];
  structuredRecords: StructuredCEORecord[];
  externalSummaries: ExternalSummaryRecord[];
  constitutions: ConstitutionVersion[];
  profiles: CEOProfile[];
  boardImports: StoredBoardContext[];
  jointCorrections: StoredJointCorrection[];
  auditEvents: AuditEvent[];
  exportManifests: DataExportManifest[];
  importResults: DataImportResult[];
  meta: DatabaseMetaRecord[];
}

export interface FullBackupPayload {
  package_type: "full_private_backup";
  database_version: number;
  contains_sensitive_data: true;
  warning: string;
  data: FullBackupData;
}

export type DataExportPackage = ContractEnvelope<FullBackupPayload>;

export interface RestorePreflight {
  report: DataImportResult;
  package?: DataExportPackage;
}

async function readAllData(database: CEOOSDatabase): Promise<FullBackupData> {
  const [
    privateSources,
    structuredRecords,
    externalSummaries,
    constitutions,
    profiles,
    boardImports,
    jointCorrections,
    auditEvents,
    exportManifests,
    importResults,
    meta,
  ] = await Promise.all([
    database.privateSources.toArray(),
    database.structuredRecords.toArray(),
    database.externalSummaries.toArray(),
    database.constitutions.toArray(),
    database.profiles.toArray(),
    database.boardImports.toArray(),
    database.jointCorrections.toArray(),
    database.auditEvents.toArray(),
    database.exportManifests.toArray(),
    database.importResults.toArray(),
    database.meta.toArray(),
  ]);
  return {
    privateSources,
    structuredRecords,
    externalSummaries,
    constitutions,
    profiles,
    boardImports,
    jointCorrections,
    auditEvents,
    exportManifests,
    importResults,
    meta,
  };
}

export async function createFullBackup(
  database: CEOOSDatabase,
  now = new Date().toISOString(),
): Promise<DataExportPackage> {
  const manifest: DataExportManifest = {
    id: crypto.randomUUID(),
    schemaVersion: CONTRACT_SCHEMA_VERSION,
    createdAt: now,
    exportType: "full_private_backup",
    status: "confirmed",
    includedRecordIds: [],
    excludedFields: [],
    sensitive: true,
    warningAcknowledged: true,
  };
  await database.exportManifests.put(manifest);
  const data = await readAllData(database);
  const includedRecordIds = [
    ...data.privateSources,
    ...data.structuredRecords,
    ...data.externalSummaries,
    ...data.constitutions,
    ...data.profiles,
    ...data.boardImports,
    ...data.jointCorrections,
    ...data.auditEvents,
  ].map((record) => record.id);
  const payload: FullBackupPayload = {
    package_type: "full_private_backup",
    database_version: DATABASE_VERSION,
    contains_sensitive_data: true,
    warning: FULL_BACKUP_WARNING,
    data,
  };
  const envelope: DataExportPackage = {
    schema_name: "data-export-package",
    schema_version: CONTRACT_SCHEMA_VERSION,
    contract_id: crypto.randomUUID(),
    produced_at: now,
    producer: "ceo-os",
    payload_hash: await sha256(payload),
    payload,
  };
  await database.transaction("rw", database.exportManifests, database.auditEvents, async () => {
    await database.exportManifests.put({
      ...manifest,
      status: "completed",
      includedRecordIds,
      payloadHash: envelope.payload_hash,
    });
    await database.auditEvents.put(
      createAuditEvent({
        entityType: "DataExportPackage",
        entityId: envelope.contract_id,
        action: "export",
        actor: "user",
        reason: "用户创建包含敏感数据的完整本地备份。",
        now,
      }),
    );
  });
  return envelope;
}

function allIncomingRecords(data: FullBackupData): Array<{
  id: string;
  updatedAt?: string;
  entityType: string;
}> {
  const groups: Array<[string, Array<{ id: string; updatedAt?: string }>]> = [
    ["PrivateSourceRecord", data.privateSources],
    ["StructuredCEORecord", data.structuredRecords],
    ["ExternalSummaryRecord", data.externalSummaries],
    ["ConstitutionVersion", data.constitutions],
    ["CEOProfile", data.profiles],
    ["StoredJointCorrection", data.jointCorrections],
  ];
  return groups.flatMap(([entityType, records]) =>
    records.map((record) => ({
      id: record.id,
      ...(record.updatedAt ? { updatedAt: record.updatedAt } : {}),
      entityType,
    })),
  );
}

export async function preflightFullRestore(
  database: CEOOSDatabase,
  value: unknown,
  sourceFileName: string,
  now = new Date().toISOString(),
): Promise<RestorePreflight> {
  const validation = validateContract<DataExportPackage>(value, "data-export-package");
  if (!validation.ok) {
    return {
      report: {
        id: crypto.randomUUID(),
        schemaVersion: CONTRACT_SCHEMA_VERSION,
        importedAt: now,
        schemaName: "data-export-package",
        status: "rejected",
        sourceFileName,
        added: 0,
        skipped: 0,
        conflicts: validation.errors
          .filter((item) => item.code === RuleErrorCode.UNSUPPORTED_CONTRACT_VERSION)
          .map(() => ({
            entityType: "DataExportPackage",
            incomingId: "unknown",
            reason: "unsupported_version" as const,
          })),
        errors: validation.errors,
      },
    };
  }

  const incoming = validation.value;
  if (!(await verifyPayloadHash(incoming))) {
    return {
      report: {
        id: crypto.randomUUID(),
        schemaVersion: CONTRACT_SCHEMA_VERSION,
        importedAt: now,
        schemaName: incoming.schema_name,
        status: "rejected",
        sourceFileName,
        added: 0,
        skipped: 0,
        conflicts: [],
        errors: [
          {
            code: RuleErrorCode.CONTRACT_HASH_MISMATCH,
            path: "/payload_hash",
            message: "payload_hash 与规范化 payload 不一致，备份可能已损坏或被修改。",
          },
        ],
      },
    };
  }
  const localUpdated = new Map<string, string>();
  const localGroups = await Promise.all([
    database.privateSources.toArray(),
    database.structuredRecords.toArray(),
    database.externalSummaries.toArray(),
    database.constitutions.toArray(),
    database.profiles.toArray(),
    database.jointCorrections.toArray(),
  ]);
  for (const record of localGroups.flat()) {
    localUpdated.set(record.id, record.updatedAt);
  }
  const conflicts = allIncomingRecords(incoming.payload.data)
    .filter((record) => {
      const localTime = localUpdated.get(record.id);
      return (
        localTime !== undefined &&
        record.updatedAt !== undefined &&
        new Date(localTime).getTime() > new Date(record.updatedAt).getTime()
      );
    })
    .map((record) => ({
      entityType: record.entityType,
      incomingId: record.id,
      localId: record.id,
      reason: "local_newer" as const,
    }));

  return {
    package: incoming,
    report: {
      id: crypto.randomUUID(),
      schemaVersion: CONTRACT_SCHEMA_VERSION,
      importedAt: now,
      schemaName: incoming.schema_name,
      status: conflicts.length > 0 ? "needs_confirmation" : "valid",
      sourceFileName,
      added: allIncomingRecords(incoming.payload.data).length,
      skipped: 0,
      conflicts,
      errors: conflicts.map(() => ({
        code: RuleErrorCode.IMPORT_LOCAL_NEWER_CONFLICT,
        path: "/payload/data",
        message: "本地存在更新时间更晚的记录；恢复前必须明确确认覆盖。",
      })),
    },
  };
}

export async function commitFullRestore(
  database: CEOOSDatabase,
  preflight: RestorePreflight,
  confirmed: boolean,
): Promise<DataImportResult> {
  if (!preflight.package || preflight.report.status === "rejected") {
    throw new Error(`${RuleErrorCode.RESTORE_PREFLIGHT_FAILED}: 恢复预检未通过。`);
  }
  if (!confirmed) {
    throw new Error(`${RuleErrorCode.RESTORE_CONFIRMATION_REQUIRED}: 恢复必须由用户确认。`);
  }
  const data = preflight.package.payload.data;
  const normalizedStructuredRecords = data.structuredRecords.map((record) => {
    const normalized = structuredClone(record) as StructuredCEORecord & Record<string, unknown>;
    normalizeRound03StructuredRecord(normalized);
    return normalized;
  });
  const normalizedProfiles = data.profiles.map((record) => {
    const normalized = structuredClone(record) as CEOProfile & Record<string, unknown>;
    normalizeRound03ProfileRecord(normalized);
    return normalized;
  });
  const committed: DataImportResult = {
    ...preflight.report,
    status: "committed",
  };
  await database.transaction(
    "rw",
    [
      database.privateSources,
      database.structuredRecords,
      database.externalSummaries,
      database.constitutions,
      database.profiles,
      database.boardImports,
      database.jointCorrections,
      database.auditEvents,
      database.exportManifests,
      database.importResults,
      database.meta,
    ],
    async () => {
      await Promise.all(database.tables.map((table) => table.clear()));
      await database.privateSources.bulkPut(data.privateSources);
      await database.structuredRecords.bulkPut(normalizedStructuredRecords);
      await database.externalSummaries.bulkPut(data.externalSummaries);
      await database.constitutions.bulkPut(data.constitutions);
      await database.profiles.bulkPut(normalizedProfiles);
      await database.boardImports.bulkPut(data.boardImports);
      await database.jointCorrections.bulkPut(data.jointCorrections);
      await database.auditEvents.bulkPut(data.auditEvents);
      await database.exportManifests.bulkPut(data.exportManifests);
      await database.importResults.bulkPut(data.importResults);
      await database.meta.bulkPut(data.meta);
      await database.importResults.put(committed);
      await database.auditEvents.put(
        createAuditEvent({
          entityType: "DataExportPackage",
          entityId: preflight.package!.contract_id,
          action: "restore",
          actor: "user",
          reason: "用户确认后原子恢复完整本地备份。",
          now: committed.importedAt,
        }),
      );
    },
  );
  return committed;
}
